import { useQuery } from '@tanstack/react-query';
import { supabase } from '../services/supabase';

export interface DonationRow {
  id: string;
  user_id: string | null;
  donor_name: string | null;
  donor_email: string | null;
  amount: number;
  currency: string;
  category: string | null;
  payment_method: string | null;
  created_at: string;
  status: string | null;
  user?: { full_name?: string | null; email?: string | null } | null;
}

const LIMIT = 200;

export type DonationFilterTarget =
  | { donorType: 'user'; donorValue: string }
  | { donorType: 'email'; donorValue: string }
  | { donorType: 'name'; donorValue: string };

export function useDonations(enabled: boolean, options?: { fromDate?: string; toDate?: string; target?: DonationFilterTarget }) {
  return useQuery({
    queryKey: [
      'dashboard',
      'donations',
      options?.fromDate ?? '',
      options?.toDate ?? '',
      options?.target?.donorType ?? '',
      options?.target?.donorValue ?? '',
    ],
    queryFn: async (): Promise<DonationRow[]> => {
      let query = supabase
        .from('donations')
        .select('id, user_id, donor_name, donor_email, amount, currency, category, payment_method, created_at, status')
        .order('created_at', { ascending: false })
        .limit(LIMIT);

      if (options?.fromDate) {
        query = query.gte('created_at', `${options.fromDate}T00:00:00`);
      }
      if (options?.toDate) {
        query = query.lte('created_at', `${options.toDate}T23:59:59`);
      }
      if (options?.target?.donorType === 'user') {
        query = query.eq('user_id', options.target.donorValue);
      } else if (options?.target?.donorType === 'email') {
        query = query.ilike('donor_email', options.target.donorValue);
      } else if (options?.target?.donorType === 'name') {
        query = query.ilike('donor_name', options.target.donorValue);
      }

      const { data, error } = await query;
      if (error) throw error;

      const rows = (data ?? []) as Array<DonationRow & { user_id?: string | null }>;
      const userIds = Array.from(new Set(rows.map((r) => r.user_id).filter(Boolean))) as string[];
      let userMap: Record<string, { full_name?: string | null; email?: string | null }> = {};
      if (userIds.length > 0) {
        const { data: users } = await supabase
          .from('users')
          .select('id, full_name, email')
          .in('id', userIds);
        (users ?? []).forEach((u: { id: string; full_name?: string | null; email?: string | null }) => {
          userMap[u.id] = { full_name: u.full_name, email: u.email };
        });
      }
      return rows.map((r) => ({
        ...r,
        amount: Number(r.amount),
        currency: r.currency ?? 'USD',
        donor_name: r.donor_name ?? null,
        donor_email: r.donor_email ?? null,
        user: r.user_id ? userMap[r.user_id] ?? null : null,
      }));
    },
    enabled,
  });
}

export function useDonationsSummary(enabled: boolean, options?: { fromDate?: string; toDate?: string }) {
  return useQuery({
    queryKey: ['dashboard', 'donations-summary', options?.fromDate ?? '', options?.toDate ?? ''],
    queryFn: async () => {
      let query = supabase.from('donations').select('amount, created_at').not('status', 'in', '(pending,deleted,removed)');
      if (options?.fromDate) {
        query = query.gte('created_at', `${options.fromDate}T00:00:00`);
      }
      if (options?.toDate) {
        query = query.lte('created_at', `${options.toDate}T23:59:59`);
      }
      const { data, error } = await query;
      if (error) throw error;
      const total = (data ?? []).reduce((sum: number, row: { amount: number }) => sum + Number(row.amount || 0), 0);
      const byWeek: Record<string, number> = {};
      (data ?? []).forEach((row: { amount: number; created_at: string }) => {
        const d = new Date(row.created_at);
        const weekStart = new Date(d);
        weekStart.setDate(d.getDate() - d.getDay());
        const key = weekStart.toISOString().slice(0, 10);
        byWeek[key] = (byWeek[key] ?? 0) + Number(row.amount || 0);
      });
      return { total, byWeek: Object.entries(byWeek).sort((a, b) => a[0].localeCompare(b[0])) };
    },
    enabled,
  });
}
